import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { StatsigClient as StatsigJsClient } from "@statsig/js-client";
import { useClientBootstrapInit } from "@statsig/react-bindings";
import {
  ENVIRONMENT,
  STATSIG_ADS_MANAGER_DEDICATED_PROJECT_SDK_KEY,
  STATSIG_SDK_KEY,
  StatsigCustomId,
} from "src/lib/constants";

import { getClientEventsServiceStatsigNetworkConfig } from "@/lib/clientEventsServiceConfig";
import { getCookie, setCookie, STATSIG_STABLE_ID_COOKIE } from "@/lib/cookie";

import type { StatsigClient, StatsigOptions } from "@statsig/react-bindings";
import type { StatsigBootstrapData } from "@/lib/statsigBootstrap";

type StatsigNetworkConfig = NonNullable<StatsigOptions["networkConfig"]>;
type StatsigLogEvent = StatsigClient["logEvent"];
type StatsigLogEventArgs = Parameters<StatsigLogEvent>;
type StatsigLogEventMetadata = Record<string, string | undefined> | null | undefined;
type StatsigClientWithUserContext = Pick<
  StatsigClient,
  "dataAdapter" | "getContext" | "updateUserSync"
>;
type StatsigContextWithStableID = ReturnType<StatsigClientWithUserContext["getContext"]> & {
  stableID?: string;
};

export const StatsigBootstrapReadinessContext = createContext(false);

export function useIsStatsigBootstrapCurrent() {
  return useContext(StatsigBootstrapReadinessContext);
}

function useAppliedStatsigBootstrapStore(initialStatsigBootstrap: StatsigBootstrapData) {
  const appliedStatsigBootstrapRef = useRef(initialStatsigBootstrap);
  const listenersRef = useRef(new Set<() => void>());
  const subscribe = useCallback((listener: () => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);
  const getSnapshot = useCallback(() => appliedStatsigBootstrapRef.current, []);
  const appliedStatsigBootstrap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const markStatsigBootstrapApplied = useCallback((statsigBootstrap: StatsigBootstrapData) => {
    if (appliedStatsigBootstrapRef.current === statsigBootstrap) {
      return;
    }
    appliedStatsigBootstrapRef.current = statsigBootstrap;
    listenersRef.current.forEach((listener) => listener());
  }, []);

  return { appliedStatsigBootstrap, markStatsigBootstrapApplied };
}

function getStatsigEnvironmentTier() {
  return ENVIRONMENT === "prod" ? "production" : ENVIRONMENT;
}

function getStatsigNetworkConfig(
  clientEventsServiceStatsigNetworkConfig: StatsigNetworkConfig | undefined,
): StatsigNetworkConfig {
  return {
    ...clientEventsServiceStatsigNetworkConfig,
    initializeUrl:
      ENVIRONMENT === "prod" ?
        "https://ab.chatgpt.com/v1/initialize"
      : "https://ab.chatgpt-staging.com/v1/initialize",
    sdkExceptionUrl:
      ENVIRONMENT === "prod" ?
        "https://ab.chatgpt.com/v1/sdk_exception"
      : "https://ab.chatgpt-staging.com/v1/sdk_exception",
  };
}

function createAdsManagerStatsigClient(
  statsigBootstrap: StatsigBootstrapData,
  networkConfig: StatsigNetworkConfig,
): StatsigJsClient | undefined {
  if (!STATSIG_ADS_MANAGER_DEDICATED_PROJECT_SDK_KEY || typeof window === "undefined") {
    return undefined;
  }

  const client = new StatsigJsClient(
    STATSIG_ADS_MANAGER_DEDICATED_PROJECT_SDK_KEY,
    statsigBootstrap.user,
    {
      environment: { tier: getStatsigEnvironmentTier() },
      loggingEnabled: "always",
      networkConfig,
    },
  );
  client.initializeSync({ disableBackgroundCacheRefresh: true });
  return client;
}

function duplicateStatsigLogEvents(
  statsigClient: StatsigClient,
  adsManagerStatsigClient: StatsigJsClient | undefined,
): StatsigClient {
  const logEvent = ((...args: StatsigLogEventArgs) => {
    const metadata = getStatsigLogEventMetadata(args);
    applyAdAccountCustomIdFromMetadata(statsigClient, metadata);
    applyAdAccountCustomIdFromMetadata(adsManagerStatsigClient, metadata);
    statsigClient.logEvent(...args);

    if (adsManagerStatsigClient) {
      try {
        adsManagerStatsigClient.logEvent(...args);
      } catch (error) {
        console.warn("Failed to log Ads Manager Statsig client event", { error });
      }
    }
  }) as StatsigLogEvent;

  return new Proxy(statsigClient, {
    get(target, property, receiver) {
      if (property === "logEvent") {
        return logEvent;
      }

      const value = Reflect.get(target, property, receiver);
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

function getStatsigLogEventMetadata(args: StatsigLogEventArgs): StatsigLogEventMetadata {
  const eventOrName = args[0];
  const metadata = args[2];
  return typeof eventOrName === "string" ? metadata : eventOrName.metadata;
}

function applyAdAccountCustomIdFromMetadata(
  statsigClient: StatsigClientWithUserContext | undefined,
  metadata: StatsigLogEventMetadata,
) {
  const adAccountId = metadata?.[StatsigCustomId.AD_ACCOUNT_ID]?.trim();
  if (!adAccountId || !statsigClient) {
    return;
  }

  try {
    const { user, values } = statsigClient.getContext();
    if (user.customIDs?.[StatsigCustomId.AD_ACCOUNT_ID] === adAccountId) {
      return;
    }

    const updatedUser = {
      ...user,
      customIDs: {
        ...user.customIDs,
        [StatsigCustomId.AD_ACCOUNT_ID]: adAccountId,
      },
    };

    if (values) {
      statsigClient.dataAdapter.setData(JSON.stringify({ ...values, user: updatedUser }));
    }

    statsigClient.updateUserSync(updatedUser, { disableBackgroundCacheRefresh: true });
  } catch (error) {
    console.warn("Failed to apply Ads Manager Statsig custom ID", { error });
  }
}

function getStableIDFromStatsigClient(
  statsigClient: StatsigClientWithUserContext | undefined,
): string | undefined {
  try {
    const context = statsigClient?.getContext() as StatsigContextWithStableID | undefined;
    return context?.stableID;
  } catch (error) {
    console.warn("Failed to read Statsig stable ID", { error });
    return undefined;
  }
}

export function useStatsigClient(
  statsigBootstrap: StatsigBootstrapData,
  onStatsigStableIDCookieChanged?: () => void,
) {
  const clientEventsServiceStatsigNetworkConfig = getClientEventsServiceStatsigNetworkConfig(
    import.meta.env.CLIENT_EVENTS_SERVICE_DOMAIN,
    ENVIRONMENT,
  );
  const networkConfig = getStatsigNetworkConfig(clientEventsServiceStatsigNetworkConfig);
  const statsigClient = useClientBootstrapInit(
    STATSIG_SDK_KEY,
    statsigBootstrap.user,
    statsigBootstrap.initializeValues,
    {
      // OpenAI uses "prod" for the environment name, but our statsig config expects "production", other environment names are the same.
      environment: { tier: getStatsigEnvironmentTier() },
      networkConfig,
    },
  );
  const [adsManagerStatsigClient] = useState(() =>
    createAdsManagerStatsigClient(statsigBootstrap, networkConfig),
  );
  const { appliedStatsigBootstrap, markStatsigBootstrapApplied } =
    useAppliedStatsigBootstrapStore(statsigBootstrap);
  const isStatsigBootstrapCurrent = appliedStatsigBootstrap === statsigBootstrap;
  const hasAppliedBootstrapData = useRef(false);

  useEffect(() => {
    // useClientBootstrapInit only applies bootstrap data when the client is first constructed.
    // Loader revalidation can change ad account scoping, so we re-apply server bootstrap values and user.
    if (!hasAppliedBootstrapData.current) {
      hasAppliedBootstrapData.current = true;
      return;
    }
    statsigClient.dataAdapter.setData(statsigBootstrap.initializeValues);
    statsigClient.updateUserSync(statsigBootstrap.user);
    adsManagerStatsigClient?.updateUserSync(statsigBootstrap.user, {
      disableBackgroundCacheRefresh: true,
    });
    markStatsigBootstrapApplied(statsigBootstrap);
  }, [adsManagerStatsigClient, markStatsigBootstrapApplied, statsigBootstrap, statsigClient]);

  useEffect(() => {
    return () => {
      void adsManagerStatsigClient?.shutdown();
    };
  }, [adsManagerStatsigClient]);

  useEffect(() => {
    const stableID =
      getStableIDFromStatsigClient(statsigClient) ??
      getStableIDFromStatsigClient(adsManagerStatsigClient);
    if (!stableID || getCookie(STATSIG_STABLE_ID_COOKIE) === stableID) {
      return;
    }

    setCookie(STATSIG_STABLE_ID_COOKIE, stableID);
    onStatsigStableIDCookieChanged?.();
  }, [adsManagerStatsigClient, onStatsigStableIDCookieChanged, statsigClient]);

  const duplicatedStatsigClient = useMemo(
    () => duplicateStatsigLogEvents(statsigClient, adsManagerStatsigClient),
    [adsManagerStatsigClient, statsigClient],
  );

  return {
    adsManagerStatsigClient,
    isStatsigBootstrapCurrent,
    statsigClient: duplicatedStatsigClient,
  };
}
